import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  CONDITION_OPERATOR_OPTIONS,
  RETRY_ATTEMPT_OPTIONS,
  RETRY_INTERVAL_OPTIONS,
  applyJobDerivedFields,
  buildAutomationScopeKey,
  findDuplicateAutomationRule,
  formatConditionLine,
  formatConditionsPreview,
  formatRetryPreview,
  formatSchedulePreview,
  getAutomatedJobLabel,
  getAutomatedJobReference,
  getAvailableConditionFields,
  getAutomatedJobsForScope,
  getConditionValueOptions,
  getConnectedConfigLabel,
  getConnectedConfigReference,
  getConnectedConfigsForJob,
  getAutomationModuleLabel,
  getRecordTypeLabel,
  getRecordTypesForModule,
  getReferenceAvailabilityLabel,
  getScheduleOptionsForJob,
  isAutomatedJobAvailable,
  isConnectedConfigAvailable,
  isScheduleAvailable,
  jobRequiresConnectedConfig,
  jobRequiresSchedule,
  jobSupportsConditions,
  jobSupportsRetry,
  namesMatch,
  nextAutomationRuleId,
  normalizeAutomationForm,
  operatorIsMulti,
} from './automationConfig';
import { emitConfigurationHistoryBatch } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

const TRACKED_FIELDS = [
  'ruleName',
  'module',
  'recordType',
  'automatedJob',
  'connectedConfig',
  'schedule',
  'maxRetryAttempts',
  'retryInterval',
  'conditions',
];

export function automationActor() {
  return { ...ACTOR };
}

export function canAccessAutomationConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewAutomationConfiguration(auth) {
  return canAccessAutomationConfiguration(auth);
}

export function canAddAutomationRule(auth) {
  return canAccessAutomationConfiguration(auth);
}

export function canEditAutomationRule(auth) {
  return canAccessAutomationConfiguration(auth);
}

export function canSaveAutomationRule(auth) {
  return canAccessAutomationConfiguration(auth);
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (key && !result.errors[key]) result.errors[key] = message;
}

function isBlank(value) {
  return value == null || !String(value).trim();
}

function conditionValues(condition) {
  return [...new Set((condition?.values || []).map((value) => String(value || '').trim()).filter(Boolean))];
}

function conditionFingerprint(condition) {
  return [
    condition.field || '',
    condition.operator || '',
    conditionValues(condition).slice().sort().join(','),
  ].join('|');
}

function conditionsCannotAllBeTrue(conditions = []) {
  const byField = {};
  conditions.forEach((condition) => {
    if (!condition.field || !condition.operator) return;
    const values = conditionValues(condition);
    if (!values.length) return;
    if (!byField[condition.field]) byField[condition.field] = [];
    byField[condition.field].push({ operator: condition.operator, values });
  });

  return Object.values(byField).some((entries) => {
    const equals = [...new Set(entries.filter((entry) => entry.operator === 'equals').flatMap((entry) => entry.values))];
    if (equals.length > 1) return true;
    const anyOf = entries.filter((entry) => entry.operator === 'is_any_of').map((entry) => entry.values);
    const positive = anyOf.length
      ? anyOf.reduce((acc, set) => acc.filter((value) => set.includes(value)))
      : equals;
    const uniquePositives = [...new Set([...equals, ...positive])];
    return entries.some((entry) => {
      if (entry.operator === 'does_not_equal' && uniquePositives.includes(entry.values[0])) return true;
      if (entry.operator === 'is_not_any_of' && uniquePositives.length && uniquePositives.every((value) => entry.values.includes(value))) {
        return true;
      }
      if (entry.operator === 'is_any_of' && equals.length && !entry.values.some((value) => equals.includes(value))) return true;
      return false;
    });
  });
}

export function collectAutomationSaveErrors(formData, allRules, excludeId, options = {}) {
  const form = applyJobDerivedFields(normalizeAutomationForm(formData));
  const result = { errors: {}, summary: [] };
  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeAutomationForm);

  if (options.canSave === false) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }
  if (options.checksAvailable === false) {
    addError(result, 'checksUnavailable', 'Required checks could not be completed. Try again.');
    return result;
  }

  if (isBlank(form.ruleName)) addError(result, 'ruleName', 'Enter a unique Rule Name.');
  else if (others.some((rule) => namesMatch(rule.ruleName, form.ruleName))) {
    addError(result, 'ruleName', 'Enter a unique Rule Name.');
  }

  if (!form.module) addError(result, 'module', 'Select a Module.');

  const recordTypes = getRecordTypesForModule(form.module);
  if (!form.recordType) addError(result, 'recordType', 'Select a compatible Record Type.');
  else if (form.module && !recordTypes.some((option) => option.value === form.recordType)) {
    addError(result, 'recordType', 'Select a compatible Record Type.');
  }

  const jobs = getAutomatedJobsForScope(form.module, form.recordType);
  if (!form.automatedJob) {
    addError(result, 'automatedJob', jobs.length ? 'Select a compatible Automated Job.' : 'No compatible choices available');
  } else if (form.module && form.recordType && !jobs.some((option) => option.value === form.automatedJob) && !isAutomatedJobAvailable(form.automatedJob)) {
    addError(result, 'automatedJob', 'This reference is unavailable or incompatible.');
  } else if (form.module && form.recordType && !jobs.some((option) => option.value === form.automatedJob)) {
    addError(result, 'automatedJob', 'Select a compatible Automated Job.');
  } else if (form.automatedJob && !isAutomatedJobAvailable(form.automatedJob)) {
    addError(result, 'automatedJob', 'This reference is unavailable or incompatible.');
  }

  if (jobRequiresConnectedConfig(form.automatedJob)) {
    const configs = getConnectedConfigsForJob(form.automatedJob);
    if (!form.connectedConfig) {
      addError(result, 'connectedConfig', configs.length ? 'Select a compatible Connected Config.' : 'No compatible choices available');
    } else if (!isConnectedConfigAvailable(form.connectedConfig)) {
      addError(result, 'connectedConfig', 'This reference is unavailable or incompatible.');
    } else if (!configs.some((option) => option.value === form.connectedConfig)) {
      addError(result, 'connectedConfig', 'Select a compatible Connected Config.');
    }
  }

  if (jobRequiresSchedule(form.automatedJob)) {
    const schedules = getScheduleOptionsForJob(form.automatedJob);
    if (!form.schedule) {
      addError(result, 'schedule', schedules.length ? 'Select a Schedule.' : 'No compatible choices available');
    } else if (!isScheduleAvailable(form.schedule)) {
      addError(result, 'schedule', 'This reference is unavailable or incompatible.');
    } else if (!schedules.some((option) => option.value === form.schedule)) {
      addError(result, 'schedule', 'Select a Schedule.');
    }
  }

  if (jobSupportsRetry(form.automatedJob)) {
    if (!RETRY_ATTEMPT_OPTIONS.some((option) => option.value === String(form.maxRetryAttempts))) {
      addError(result, 'maxRetryAttempts', 'Select compatible retry settings.');
    } else if (Number(form.maxRetryAttempts) > 0 && !RETRY_INTERVAL_OPTIONS.some((option) => option.value === form.retryInterval)) {
      addError(result, 'retryInterval', 'Select compatible retry settings.');
    }
  }

  if (isBlank(form.changeReason)) addError(result, 'changeReason', 'Enter a Change Reason.');

  const dup = findDuplicateAutomationRule(others, form);
  if (dup) addError(result, 'duplicate', `An Automation Rule already exists for this scope: ${dup.id}.`);

  if (excludeId && options.originalScopeKey && buildAutomationScopeKey(form) !== options.originalScopeKey) {
    addError(result, 'scope', 'The saved scope cannot be changed.');
  }

  if (jobSupportsConditions(form.automatedJob)) {
    const availableFields = getAvailableConditionFields(form).map((option) => option.value);
    const seenFingerprints = [];
    (form.conditions || []).forEach((condition, index) => {
      const key = `condition_${index}`;
      const values = conditionValues(condition);
      if (!condition.field || !condition.operator || !values.length) {
        addError(result, key, 'Complete or remove this Condition.');
        return;
      }
      if (!CONDITION_OPERATOR_OPTIONS.some((option) => option.value === condition.operator)) {
        addError(result, key, 'This Field, Operator or Value is incompatible.');
      }
      if (!operatorIsMulti(condition.operator) && values.length !== 1) {
        addError(result, key, 'Complete or remove this Condition.');
      }
      if (!availableFields.includes(condition.field)) {
        addError(result, key, 'This Field, Operator or Value is incompatible.');
      }
      const valueOptions = getConditionValueOptions(condition.field).map((option) => option.value);
      if (values.some((value) => valueOptions.length && !valueOptions.includes(value))) {
        addError(result, key, 'This Field, Operator or Value is incompatible.');
      }
      const fingerprint = conditionFingerprint(condition);
      if (seenFingerprints.includes(fingerprint)) addError(result, key, 'This Condition is already included.');
      seenFingerprints.push(fingerprint);
    });
    if (conditionsCannotAllBeTrue(form.conditions)) {
      addError(result, 'conditions', 'These Conditions cannot all be true.');
    }
  }

  if (excludeId && options.originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current && (current.lastUpdated || current.modifiedOn) && (current.lastUpdated || current.modifiedOn) !== options.originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule has changed. Review the latest saved Rule.');
    }
  }

  return result;
}

export function ruleSnapshotFromForm(formData) {
  const form = applyJobDerivedFields(normalizeAutomationForm(formData));
  return {
    ruleName: String(form.ruleName || '').trim(),
    module: form.module,
    recordType: form.recordType,
    automatedJob: form.automatedJob,
    connectedConfig: jobRequiresConnectedConfig(form.automatedJob) ? form.connectedConfig : '',
    schedule: jobRequiresSchedule(form.automatedJob) ? form.schedule : '',
    maxRetryAttempts: jobSupportsRetry(form.automatedJob) ? String(form.maxRetryAttempts) : '',
    retryInterval: jobSupportsRetry(form.automatedJob) && Number(form.maxRetryAttempts) > 0 ? form.retryInterval : '',
    conditions: jobSupportsConditions(form.automatedJob)
      ? (form.conditions || []).map((condition) => ({
        id: condition.id,
        field: condition.field,
        operator: condition.operator,
        values: conditionValues(condition),
      }))
      : [],
    changeReason: String(form.changeReason || '').trim(),
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeAutomationForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeAutomationForm({ ...rule, pendingDraft: null });
}

function fieldLabel(key) {
  const labels = {
    ruleName: 'Rule Name',
    module: 'Module',
    recordType: 'Record Type',
    automatedJob: 'Automated Job',
    connectedConfig: 'Connected Config',
    schedule: 'Schedule',
    maxRetryAttempts: 'Maximum Retry Attempts',
    retryInterval: 'Retry Interval',
    conditions: 'Conditions',
  };
  return labels[key] || key;
}

function displayFieldValue(key, value, context = {}) {
  if (key === 'conditions') return formatConditionsPreview(value || [], context.automatedJob).text;
  if (value == null || value === '') return '—';
  if (key === 'module') return getAutomationModuleLabel(value);
  if (key === 'recordType') return getRecordTypeLabel(value);
  if (key === 'automatedJob') return getAutomatedJobLabel(value);
  if (key === 'connectedConfig') return getConnectedConfigLabel(value);
  if (key === 'schedule') return formatSchedulePreview({ ...context, schedule: value });
  return String(value);
}

export function diffRuleSnapshots(prev, next) {
  const changes = [];
  TRACKED_FIELDS.forEach((key) => {
    const oldDisplay = displayFieldValue(key, prev?.[key], prev || {});
    const newDisplay = displayFieldValue(key, next?.[key], next || {});
    if (oldDisplay !== newDisplay) {
      changes.push({ field: fieldLabel(key), previousValue: oldDisplay, newValue: newDisplay });
    }
  });
  return changes;
}

function snapshotsEqual(a, b) {
  return diffRuleSnapshots(a, b).length === 0;
}

export function appendConfigurationHistory(rule, action, changes, status, version, changeReason = '') {
  const ts = new Date().toISOString();
  const actor = automationActor();
  const base = Array.isArray(rule.configurationHistory) ? rule.configurationHistory : [];
  const changeRows = changes.length ? changes : [{ field: '—', previousValue: '—', newValue: '—' }];
  const entries = changeRows.map((c) => ({
    ruleId: rule.id,
    version,
    action,
    field: c.field,
    previousValue: c.previousValue,
    newValue: c.newValue,
    status,
    actor: actor.name,
    role: actor.role,
    accountId: actor.accountId,
    timestamp: ts,
    changeReason,
  }));
  try {
    emitConfigurationHistoryBatch({
      rows: entries,
      module: 'Configuration — Automation',
      sourceModuleRef: 'configuration_automation',
      changedItem: rule.ruleName || rule.id,
      itemId: rule.id,
      actor: { name: actor.name, role: actor.role },
      stamp: ts,
      changeType: action === 'Add' || action === 'Created' ? 'Add' : 'Edit',
      changeReason,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  return [...base, ...entries];
}

export function appendTestHistory(rule, summary, passed) {
  const actor = automationActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const actor = automationActor();
  const nowIso = payload.lastUpdated || new Date().toISOString();

  if (!isNew) {
    const prevSnap = ruleSnapshotFromForm(publishedBaseline);
    if (snapshotsEqual(prevSnap, snapshot)) {
      return {
        rules: allRules,
        record: getPublishedRule(publishedBaseline),
        unchanged: true,
        isNew: false,
      };
    }
  }

  const id = isNew ? payload.id || nextAutomationRuleId(allRules) : publishedBaseline.id;
  const changes = isNew
    ? diffRuleSnapshots({}, snapshot)
    : diffRuleSnapshots(ruleSnapshotFromForm(publishedBaseline), snapshot);

  const record = {
    ...(isNew ? {} : getPublishedRule(publishedBaseline)),
    ...snapshot,
    id,
    version: isNew ? 1 : publishedBaseline.version || 1,
    pendingDraft: null,
    configurationHistory: appendConfigurationHistory(
      {
        id,
        ruleName: snapshot.ruleName,
        configurationHistory: publishedBaseline?.configurationHistory || [],
      },
      isNew ? 'Add' : 'Edit',
      changes,
      'saved',
      isNew ? 1 : publishedBaseline.version || 1,
      snapshot.changeReason,
    ),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor.name : publishedBaseline.addedBy || publishedBaseline.createdBy || actor.name,
    lastUpdated: nowIso,
    lastUpdatedBy: actor.name,
  };

  const rules = isNew
    ? [...allRules.filter((r) => r.id !== record.id), record]
    : allRules.map((r) => (r.id === record.id ? record : r));

  return { rules, record, unchanged: false, isNew };
}

export function saveAutomationRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectAutomationSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated:
      options.originalLastUpdated
      ?? publishedBaseline?.lastUpdated
      ?? publishedBaseline?.modifiedOn,
    originalScopeKey:
      options.originalScopeKey
      ?? (publishedBaseline ? buildAutomationScopeKey(publishedBaseline) : undefined),
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { form: 'The Rule could not be saved. Your changes have been kept for review.' },
      summary: ['The Rule could not be saved. Your changes have been kept for review.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const result = saveAutomationRule(
    allRules,
    { ...payload, changeReason: payload.changeReason || 'Save from configuration test' },
    publishedBaseline || {},
    { canSave: true, checksAvailable: true },
  );
  return result.rules;
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectAutomationSaveErrors(formData, allRules, excludeId).summary;
}

export function runAutomationRuleTest(formData, allRules) {
  const snapshot = ruleSnapshotFromForm(formData);
  const validation = collectAutomationSaveErrors(formData, allRules, formData.id || undefined);
  const lines = [
    `Module: ${getAutomationModuleLabel(snapshot.module) || '—'}`,
    `Record Type: ${getRecordTypeLabel(snapshot.recordType) || '—'}`,
    `Automated Job: ${getAutomatedJobLabel(snapshot.automatedJob) || '—'}`,
    `Job reference: ${getAutomatedJobReference(snapshot.automatedJob) || '—'}`,
    `Connected Config: ${jobRequiresConnectedConfig(snapshot.automatedJob) ? getConnectedConfigLabel(snapshot.connectedConfig) : 'Not Required'}`,
    `Config reference: ${getConnectedConfigReference(snapshot.connectedConfig) || '—'}`,
    `Schedule: ${formatSchedulePreview(snapshot)}`,
    `Retry: ${formatRetryPreview(snapshot)}`,
    `Reference availability: ${getReferenceAvailabilityLabel(snapshot.automatedJob, snapshot.connectedConfig, snapshot.schedule)}`,
  ];
  if ((snapshot.conditions || []).length) {
    lines.push('', 'Conditions:', ...snapshot.conditions.map((condition) => `• ${formatConditionLine(condition)}`));
  } else {
    lines.push('', jobSupportsConditions(snapshot.automatedJob) ? 'No Conditions' : 'Conditions: Not Applicable');
  }
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...validation.summary.map((message) => `• ${message}`)].join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no live job was started.',
    detail: [...lines, '', 'No duplicate-scope or incompatible settings detected.'].join('\n'),
  };
}

export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline) {
  return mergeRulesAfterActivate(allRules, payload, publishedBaseline);
}

export function mergeRulesAfterInactivate(allRules) {
  return allRules;
}

export function mergeRulesAfterListActivate(allRules) {
  return allRules;
}

export { normalizeAutomationForm, buildAutomationScopeKey };
